// src/pages/Concursos.tsx

import React, { useState, useEffect } from "react";
import { fetchConcursos, toggleDescartado } from "../api/concursosApi";
import { fetchFavoritosIds, toggleFavorito } from "../api/favoritosApi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../css/concursos.css";
import "../css/checkboxdropdown.css";
import "../css/amountdropdown.css";
import { useNavigate, Link, useSearchParams } from "react-router-dom";

import Select from "react-select";
import { provinciasOptions } from "../constants/provincias";
import { paisesOptions } from "../constants/paises"; // ← NUEVO

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faHeart,
  faLink,
  faLandmark,
  faMapMarkerAlt,
  faTrash,
  faUndo,
  faFileExcel,
  faCheck,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

import CheckboxDropdown from "../components/checkboxDropdown";
import AmountDropdown from "../components/amountDropdown";
import LoadingLottie from "../components/LoadingLottie";

// Para autenticación y token
import { useAuth, getAuthToken } from "../security/AuthLogin";

const Concursos: React.FC = () => {
  const { userRole } = useAuth();

  // -------------------- FUNCIÓN HELPER PARA FECHAS --------------------
  const formatearFecha = (fechaStr: string) => {
    if (!fechaStr) return "No hay fecha límite";

    try {
      const fecha = new Date(fechaStr);

      if (isNaN(fecha.getTime())) {
        throw new Error("Fecha inválida");
      }

      const day = fecha.getUTCDate().toString().padStart(2, "0");
      const month = (fecha.getUTCMonth() + 1).toString().padStart(2, "0");
      const year = fecha.getUTCFullYear();
      const hours = fecha.getUTCHours().toString().padStart(2, "0");
      const minutes = fecha.getUTCMinutes().toString().padStart(2, "0");

      return `${day}/${month}/${year} a las ${hours}:${minutes}h`;
    } catch (error) {
      console.error(
        "Error formateando fecha:",
        error,
        "Fecha recibida:",
        fechaStr
      );
      return "Fecha inválida";
    }
  };

  // -------------------- ESTADOS --------------------
  const [viewMode, setViewMode] = useState<"normal" | "descartados">("normal");
  const [concursos, setConcursos] = useState<any[]>([]);
  const [favoritosSet, setFavoritosSet] = useState<Set<string>>(new Set());
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // ----- filtros pendientes (panel) -----
  const [pendingSearchTerm, setPendingSearchTerm] = useState(
    searchParams.get("search") || ""
  );
  const [pendingTipoConcurso, setPendingTipoConcurso] = useState<string[]>(
    searchParams.getAll("tipo")
  );
  const [pendingFechaRango, setPendingFechaRango] = useState<
    [Date | null, Date | null]
  >([
    searchParams.get("fechaInicio")
      ? new Date(searchParams.get("fechaInicio")!)
      : null,
    searchParams.get("fechaFin")
      ? new Date(searchParams.get("fechaFin")!)
      : null,
  ]);
  const [pendingImporteDesde, setPendingImporteDesde] = useState(
    searchParams.get("importeDesde") || ""
  );
  const [pendingImporteHasta, setPendingImporteHasta] = useState(
    searchParams.get("importeHasta") || ""
  );
  const [pendingProvincia, setPendingProvincia] = useState<string>(
    searchParams.get("provincia") || ""
  );
  const [pendingPais, setPendingPais] = useState<string>( // ← NUEVO
    searchParams.get("pais") || ""
  );
  const [pendingEstado, setPendingEstado] = useState<string[]>(() => {
    const estadosFromParams = searchParams.getAll("estado");
    return estadosFromParams.length > 0 ? estadosFromParams : ["En plazo"];
  });
  const [pendingOrdenarPor, setPendingOrdenarPor] = useState(
    searchParams.get("ordenarPor") || ""
  );

  // ----- extraemos de searchParams para la carga -----
  const searchTerm = searchParams.get("search") || "";
  const fechaRango: [string, string] = [
    searchParams.get("fechaInicio") || "",
    searchParams.get("fechaFin") || "",
  ];
  const importeDesde = searchParams.get("importeDesde") || "";
  const importeHasta = searchParams.get("importeHasta") || "";
  const ordenarPor = searchParams.get("ordenarPor") || "";
  const provincia = searchParams.get("provincia") || "";
  const pais = searchParams.get("pais") || ""; // ← NUEVO
  const page = parseInt(searchParams.get("page") || "1", 10);

  // Opciones
  const estadoOptions = [
    "Anuncio previo",
    "En plazo",
    "Pdte adjudicación",
    /* "Resuelta", */
    "Anulada",
  ];
  const tipoConcursoOptions = [
    "Obras",
    "Servicios",
    "Suministros",
    "Mixto",
    "Concesión de obras",
    "Concesión de servicios",
  ];

  // NUEVOS ESTADOS para controlar el spinner/tick en el botón de Excel
  const [excelStatus, setExcelStatus] = useState<
    "idle" | "loading" | "success"
  >("idle");

  // -------------------- FUNCIONES --------------------

  // Carga de concursos
  const loadConcursos = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {
        page,
        limit: 10,
        search: searchTerm,
        tipo: pendingTipoConcurso,
        fechaInicio: fechaRango[0],
        fechaFin: fechaRango[1],
        importeDesde,
        importeHasta,
        ordenarPor,
        provincia,
        pais, // ← NUEVO
      };

      // Filtro estado por defecto
      params.estado = pendingEstado.length > 0 ? pendingEstado : ["En plazo"];

      // Modo descartados
      if (viewMode === "descartados") {
        params.verDescartados = "true";
      }

      const { data, total, limit } = await fetchConcursos(params);
      setConcursos(data);
      setTotalPages(Math.max(Math.ceil(total / limit), 1));

      // Ajuste de página fuera de rango
      if (page > Math.ceil(total / limit) && total > 0) {
        const newPage = Math.ceil(total / limit);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("page", String(newPage));
        setSearchParams(newSearchParams);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error desconocido al cargar los concursos.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Cargar IDs favoritos
  const loadFavoritosIds = async () => {
    try {
      const ids = await fetchFavoritosIds();
      setFavoritosSet(new Set(ids));
    } catch (error) {
      console.error("Error al cargar favoritos:", error);
    }
  };

  useEffect(() => {
    loadConcursos();
    loadFavoritosIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, viewMode]);

  // ---------- APLICAR / RESET FILTROS ----------
  const applyFilters = () => {
    const params: Record<string, string | string[]> = {};

    if (pendingSearchTerm) params["search"] = pendingSearchTerm;
    if (pendingTipoConcurso.length > 0) params["tipo"] = pendingTipoConcurso;

    if (pendingFechaRango[0]) {
      params["fechaInicio"] = new Date(
        pendingFechaRango[0].getTime() -
          pendingFechaRango[0].getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
    }
    if (pendingFechaRango[1]) {
      params["fechaFin"] = new Date(
        pendingFechaRango[1].getTime() -
          pendingFechaRango[1].getTimezoneOffset() * 60000
      )
        .toISOString()
        .split("T")[0];
    }

    if (pendingImporteDesde) params["importeDesde"] = pendingImporteDesde;
    if (pendingImporteHasta) params["importeHasta"] = pendingImporteHasta;

    if (pendingProvincia) params["provincia"] = pendingProvincia;
    if (pendingPais) params["pais"] = pendingPais; // ← NUEVO

    params["estado"] = pendingEstado.length > 0 ? pendingEstado : ["En plazo"];
    if (pendingOrdenarPor) params["ordenarPor"] = pendingOrdenarPor;

    params["page"] = "1"; // Reiniciar a la primera página
    setSearchParams(params);
  };

  const handleFilterReset = () => {
    setPendingSearchTerm("");
    setPendingTipoConcurso([]);
    setPendingFechaRango([null, null]);
    setPendingImporteDesde("");
    setPendingImporteHasta("");
    setPendingProvincia("");
    setPendingPais(""); // ← NUEVO
    setPendingEstado(["En plazo"]);
    setPendingOrdenarPor("");
    setSearchParams({});
  };

  const applyOrdenarPor = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("ordenarPor", pendingOrdenarPor);
    newSearchParams.set("page", "1");
    setSearchParams(newSearchParams);
  };

  // ---------- Paginación ----------
  const changePage = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", newPage.toString());
    setSearchParams(newSearchParams);
  };

  // ---------- Descartar / Restaurar ----------
  const handleDiscard = async (idConcurso: string) => {
    try {
      const ok = window.confirm(
        viewMode === "normal"
          ? "¿Estás seguro de descartar este concurso?"
          : "¿Deseas restaurar este concurso descartado?"
      );
      if (!ok) return;

      if (viewMode === "normal") {
        await toggleDescartado(idConcurso, "add");
      } else {
        await toggleDescartado(idConcurso, "remove");
      }
      loadConcursos();
    } catch (error) {
      console.error("Error al descartar/restaurar concurso:", error);
    }
  };

  const handleToggleFavorito = async (idConcurso: string, isFav: boolean) => {
    if (
      !window.confirm(
        isFav
          ? "¿Quieres quitar este concurso de tus favoritos?"
          : "¿Marcar como favorito?"
      )
    ) {
      return;
    }
    try {
      if (isFav) {
        await toggleFavorito(idConcurso, "remove");
        setFavoritosSet((prev) => {
          const newSet = new Set(prev);
          newSet.delete(idConcurso);
          return newSet;
        });
      } else {
        await toggleFavorito(idConcurso, "add");
        setFavoritosSet((prev) => {
          const newSet = new Set(prev);
          newSet.add(idConcurso);
          return newSet;
        });
      }
    } catch (error) {
      console.error("Error al marcar favorito:", error);
    }
  };

  // ---------- Descargar Excel ----------
  const downloadExcel = async () => {
    try {
      setExcelStatus("loading");

      const token = getAuthToken();
      if (!token) {
        alert("No hay token. Inicia sesión.");
        setExcelStatus("idle");
        return;
      }
      const queryString = searchParams.toString();
      const exportUrl = `${window.location.protocol}//${window.location.hostname}/api/concursos/export?${queryString}`;

      const response = await fetch(exportUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`Error al descargar Excel: ${response.statusText}`);
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = "concursos.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setExcelStatus("success");
      setTimeout(() => setExcelStatus("idle"), 2000);
    } catch (err) {
      console.error("Error al descargar:", err);
      setExcelStatus("idle");
    }
  };

  const trashColor = viewMode === "descartados" ? "#b90029" : "";

  const renderExcelButtonContent = () => {
    if (excelStatus === "loading") {
      return (
        <>
          <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 5 }} />
          Generando...
        </>
      );
    }
    if (excelStatus === "success") {
      return (
        <>
          <FontAwesomeIcon icon={faCheck} style={{ marginRight: 5 }} />
          ¡Descargado!
        </>
      );
    }
    return (
      <>
        <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 5 }} />
        Descargar Excel
      </>
    );
  };

  const navigate = useNavigate();

  // -------------------- RENDER --------------------
  return (
    <div className="container-concursos">
      {/* Tabs normal / descartados */}
      <div style={{ marginBottom: "1rem" }}>
        <button
          onClick={() => {
            setViewMode("normal");
            const newParams = new URLSearchParams(searchParams);
            newParams.set("page", "1");
            setSearchParams(newParams);
          }}
          style={{
            marginRight: "1rem",
            backgroundColor: viewMode === "normal" ? "#b90029" : "#555",
            color: "#fff",
          }}
        >
          Concursos
        </button>
        <button
          onClick={() => {
            setViewMode("descartados");
            const newParams = new URLSearchParams(searchParams);
            newParams.set("page", "1");
            setSearchParams(newParams);
          }}
          style={{
            backgroundColor: viewMode === "descartados" ? "#b90029" : "#555",
            color: "#fff",
          }}
        >
          Descartados
        </button>
      </div>

      {/* Ordenar */}
      <div className="ordenar-por">
        <label htmlFor="ordenarPor">Ordenar por:</label>
        <select
          id="ordenarPor"
          value={pendingOrdenarPor}
          onChange={(e) => setPendingOrdenarPor(e.target.value)}
          className="form-control"
        >
          <option value="">Seleccione...</option>
          <option value="fecha_publicacion ASC">Fecha publicación (asc)</option>
          <option value="fecha_publicacion DESC">
            Fecha publicación (desc)
          </option>
          <option value="fecha_fin_ofertas ASC">Fin ofertas (asc)</option>
          <option value="fecha_fin_ofertas DESC">Fin ofertas (desc)</option>
          <option value="importe_total ASC">Importe (asc)</option>
          <option value="importe_total DESC">Importe (desc)</option>
        </select>
        <button onClick={applyOrdenarPor} className="btn btn-primary">
          Aplicar
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros">
        <input
          type="text"
          placeholder="Buscar..."
          value={pendingSearchTerm}
          onChange={(e) => setPendingSearchTerm(e.target.value)}
          className="form-control busqueda"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyFilters();
            }
          }}
        />

        <CheckboxDropdown
          options={estadoOptions}
          selectedOptions={pendingEstado}
          onChange={setPendingEstado}
          placeholder="Seleccione estado(s)"
        />

        <CheckboxDropdown
          options={tipoConcursoOptions}
          selectedOptions={pendingTipoConcurso}
          onChange={setPendingTipoConcurso}
          placeholder="Seleccione tipo(s)"
        />

        {/* País (react-select) */}
        <Select
          options={paisesOptions}
          placeholder="País"
          isClearable
          className="pais-select"
          classNamePrefix="pais-select"
          value={
            pendingPais ? { value: pendingPais, label: pendingPais } : null
          }
          onChange={(sel) => setPendingPais(sel ? sel.value : "")}
        />

        {/* Provincia (react-select) */}
        <Select
          options={provinciasOptions}
          placeholder="Provincia"
          isClearable
          className="provincia-select"
          classNamePrefix="provincia-select"
          value={
            pendingProvincia
              ? { value: pendingProvincia, label: pendingProvincia }
              : null
          }
          onChange={(sel) => setPendingProvincia(sel ? sel.value : "")}
        />

        <AmountDropdown
          desde={pendingImporteDesde}
          hasta={pendingImporteHasta}
          onChange={(desde, hasta) => {
            setPendingImporteDesde(desde);
            setPendingImporteHasta(hasta);
          }}
          placeholder="Importe"
        />

        <DatePicker
          selected={pendingFechaRango[0] || undefined}
          onChange={(dates: [Date | null, Date | null]) =>
            setPendingFechaRango(dates)
          }
          startDate={pendingFechaRango[0] || undefined}
          endDate={pendingFechaRango[1] || undefined}
          selectsRange
          isClearable
          dateFormat="dd/MM/yyyy"
          placeholderText="Rango de fechas de publicación"
        />

        <button onClick={applyFilters} className="btn btn-primary">
          Filtrar
        </button>
        <button onClick={handleFilterReset} className="btn btn-secondary">
          Reset
        </button>
      </div>

      {/* Botones Excel y crear */}
      <div style={{ margin: "1rem 0", display: "flex", gap: "1rem" }}>
        <button
          onClick={downloadExcel}
          className="btn btn-success"
          disabled={excelStatus === "loading"}
        >
          {renderExcelButtonContent()}
        </button>
        {(userRole === "admin" || userRole === "tecnico") && (
          <button
            onClick={() => navigate("/concursos/crear")}
            className="btn btn-primary"
          >
            Crear Concurso
          </button>
        )}
      </div>

      {/* Resultados */}
      {loading ? (
        <LoadingLottie />
      ) : error ? (
        <p style={{ color: "#b90029" }}>{error}</p>
      ) : concursos.length === 0 ? (
        <p>No hay concursos disponibles.</p>
      ) : (
        <div className="listing-container">
          {concursos.map((concurso) => {
            const isFav = favoritosSet.has(concurso.id_concurso);

            return (
              <div className="listing-item" key={concurso.id_concurso}>
                <div className="info">
                  <div className="header-concurso">
                    <div className="expediente">
                      {concurso.codigo_expediente}
                    </div>
                    <div className="subheader-concurso">
                      <div
                        className={
                          "estado estado-" +
                          (concurso.estado || "")
                            .toLowerCase()
                            .replace(/ /g, "-")
                        }
                      >
                        {concurso.estado || "Sin especificar"}
                      </div>
                      <div className="publication-date">
                        {concurso.fecha_publicacion
                          ? new Date(
                              concurso.fecha_publicacion
                            ).toLocaleDateString()
                          : ""}
                      </div>
                    </div>
                  </div>

                  <div className="title">{concurso.titulo}</div>

                  <div className="details">
                    <div>
                      <FontAwesomeIcon icon={faLandmark} className="icon" />{" "}
                      {concurso.entidad_compradora}
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="icon" />{" "}
                      {concurso.pais} - {concurso.localidad}
                    </div>
                  </div>

                  <div className="extra-info">
                    {["En plazo", "Anuncio previo"].includes(concurso.estado) &&
                    concurso.fecha_fin_ofertas ? (
                      <div className="fin-plazo">
                        <span>Hasta&nbsp;</span>
                        <span>
                          {formatearFecha(concurso.fecha_fin_ofertas)}
                        </span>
                      </div>
                    ) : (
                      <div className="fin-plazo">
                        <span>No hay fecha límite</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="footer-concurso">
                  <div className="importe">
                    {parseFloat(concurso.importe_total) > 0
                      ? new Intl.NumberFormat("es-ES", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits:
                            parseFloat(concurso.importe_total) % 1 === 0
                              ? 0
                              : 2,
                        })
                          .format(parseFloat(concurso.importe_total))
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                      : concurso.importe_estimado
                      ? new Intl.NumberFormat("es-ES", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits:
                            parseFloat(concurso.importe_estimado) % 1 === 0
                              ? 0
                              : 2,
                        })
                          .format(parseFloat(concurso.importe_estimado))
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                      : "N/A"}
                  </div>

                  <div className="actions-concurso">
                    {userRole === "cliente" && (
                      <>
                        <FontAwesomeIcon
                          icon={viewMode === "normal" ? faTrash : faUndo}
                          title={
                            viewMode === "normal" ? "Descartar" : "Restaurar"
                          }
                          style={{ cursor: "pointer", color: trashColor }}
                          onClick={() => handleDiscard(concurso.id_concurso)}
                        />

                        {concurso.estado === "En plazo" && (
                          <FontAwesomeIcon
                            icon={faHeart}
                            title="Favorito"
                            style={{
                              color: isFav ? "#b90029" : "",
                            }}
                            onClick={() =>
                              handleToggleFavorito(concurso.id_concurso, isFav)
                            }
                          />
                        )}
                      </>
                    )}

                    <Link to={`/concurso/${concurso.id_concurso}`}>
                      <FontAwesomeIcon icon={faEye} title="Ver más" />
                    </Link>
                    {concurso.enlace && (
                      <a
                        href={concurso.enlace}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FontAwesomeIcon icon={faLink} title="Enlace oficial" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      <div className="pagination-concursos">
        <button
          onClick={() => changePage(Math.max(page - 1, 1))}
          disabled={page === 1}
        >
          Anterior
        </button>
        <span>
          Página {page} de {totalPages}
        </span>
        <button
          onClick={() => changePage(Math.min(page + 1, totalPages))}
          disabled={page === totalPages}
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default Concursos;
